'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import SignupHeader from '@/components/SignupHeader';
import Toast from '@/components/Toast';
import '@/styles/global.css';

export default function LoginPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      email: '',
      password: ''
    };

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setToast(null);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        setToast({ message: data.error || 'Invalid email or password. Please try again.', type: 'error' });
        setIsLoading(false);
        return;
      }

      // Store token in localStorage
      if (data.token) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
      }

      setToast({ message: 'Login successful!', type: 'success' });
      
      // Redirect based on user role after 1.5 seconds
      setTimeout(() => {
        const userRole = data.user.role;
        
        if (userRole === 'gym_owner') {
          window.location.href = '/dashboard/gym';
        } else if (userRole === 'trainer') {
          window.location.href = '/dashboard/trainer';
        } else {
          window.location.href = '/dashboard';
        }
      }, 1500);
      
    } catch (error) {
      console.error('Login error:', error);
      setToast({ message: 'Invalid email or password. Please try again.', type: 'error' });
      setIsLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#F6EFED]">
      <SignupHeader />

      <section className="join-section py-20">
        <div className="max-w-[1380px] mx-auto px-[20px]">
          <div className="grid md:grid-cols-2 gap-30">
            {/* Left Side */}
            <div className="join-left">
              <p className="eyebrow font-['Archivo',sans-serif] text-[16px] text-[#8A7296] mb-4">
                • Women-only & women-led
              </p>
              <h1 className="font-['Gotham_Rounded',sans-serif] text-[48px] font-bold text-[#2E2A3A] leading-[56px] mb-6">
                Join a women-only fitness community
              </h1>
              <p className="desc font-['Archivo',sans-serif] text-[18px] text-[#6e6672] leading-[28px]">
                Connect with certified trainers, discover inclusive gyms and
                reach your goals in a safe, empowering space.
              </p>
            </div>

            {/* Right Side - Sign In Form */}
            <div>
              <form className="signin-card" onSubmit={handleSubmit}>
                <h2>Sign in</h2>

                <label>Email Address</label>
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="e.g. you@example.com"
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}

                <label>Password</label>
                <input 
                  type="password" 
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="e.g. 8+ characters"
                  className={errors.password ? 'border-red-500' : ''}
                />
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                )}

                <div className="text-right mb-4">
                  <Link 
                    href="/forgot-password" 
                    className="font-['Archivo',sans-serif] text-[14px] text-[#8A7296] hover:text-[#7a647d] transition-colors font-medium"
                  >
                    Forgot Password?
                  </Link>
                </div>

                <button type="submit" disabled={isLoading}>
                  {isLoading ? 'Signing in...' : 'Sign in'}
                  {!isLoading && <Image src="/btn-arrow.svg" width={16} height={16} alt="arrow" className="inline-block ml-2" />}
                </button>
              </form>

              <p className="signin text-center font-['Archivo',sans-serif] text-[16px] text-[#2E2A3A] mt-8">
                Don't have an account?{' '}
                <Link href="/signup" className="text-[#8A7296] hover:text-[#7a647d] transition-colors font-medium">
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={2000}
        />
      )}
    </div>
  );
}
